<template>
  <vx-card class="v-bar-chart-card" style="max-width: 100%">
    <div>
      <!-- header tools -->
      <div class="v-bar-chart-card-header">
        <div v-if="isMobile" class="font-bold flex mb-4" style="font-size: 1em; flex-direction: column; gap: 4px">
          <div><i class="dot"></i>{{ title }}</div>
          <div class="ml-4 flex items-center gap-1">
            <slot name="right-buttons"></slot>
          </div>
        </div>
        <div v-else class="font-bold flex items-center" style="font-size: 1em">
          <div><i class="dot"></i>{{ title }}</div>
          <div class="ml-4 flex items-center gap-2">
            <slot name="right-buttons"></slot>
          </div>
        </div>
        <div style="justify-content: end; height: 38px">
          <a href="javascript:;" v-if="isMoveDetail && projectId" style="font-size: 0.85em; display: flex; align-items: center; justify-content: flex-end" @click="onClickDetail"
            >자세히 보기<feather-icon icon="ChevronRightIcon" style="width: 1.42em"
          /></a>
          <a v-if="isMoveWrite" href="javascript:;" style="font-size: 0.85em; display: flex; align-items: center; justify-content: flex-end" @click="onClickWrite"
            >작성페이지 이동<feather-icon icon="ChevronRightIcon" style="width: 1.42em"
          /></a>
          <vs-select v-if="useSelectYear" autocomplete style="width: 100px" v-model="select.year" @input="onSelect('year')">
            <vs-select-item v-for="option of yearOptions" :key="option.value" :value="option.value" :text="option.label" />
          </vs-select>
        </div>
      </div>
      <div class="w-full">
        <slot name="header" :isMobile="isMobile" :categoryCounts="categoryCounts">
          <div class="flex gap-4">
            <div class="flex items-center gap-2">
              <div class="circle purple" />
              <div style="color: #757575">{{ totalLabel }}</div>
            </div>
            <div style="font-weight: bold; font-size: 16px">{{ totalCount }}</div>
          </div>
          <div v-if="!isMobile" class="flex gap-4">
            <div class="flex items-center gap-2">
              <div class="circle grey" />
              <div style="color: #757575">{{ countLabel }}</div>
            </div>
            <div class="flex gap-3 items-center">
              <div v-for="item of categoryCounts" :key="item.name" class="flex items-center gap-2" style="font-size: 16px">
                <div v-if="item.prefix">{{ item.prefix }}</div>
                <div>{{ item.name }} :</div>
                <div style="font-weight: bold">{{ item.count.toLocaleString() }}</div>
                <div v-if="item.suffix">{{ item.suffix }}</div>
              </div>
            </div>
          </div>
          <div v-if="isMobile" class="mobile-layout">
            <div class="flex items-center gap-2 mb-2" style="align-self: flex-start">
              <div class="circle grey" />
              <div class="count-label" style="color: #757575">{{ countLabel }}</div>
            </div>
            <div class="mobile-counts">
              <div v-for="item of categoryCounts" :key="item.name" class="mobile-count-item">
                <span v-if="item.prefix">{{ item.prefix }}</span>
                <span>{{ item.name }} :</span>
                <span class="count-value">{{ item.count.toLocaleString() }}</span>
                <span v-if="item.suffix">{{ item.suffix }}</span>
              </div>
            </div>
          </div>
        </slot>
      </div>
      <div class="w-full chart-container">
        <!-- 로딩 상태 표시 -->
        <div v-if="loading" class="loading-container">
          <div class="loading-spinner"></div>
          <div class="loading-text">데이터를 불러오는 중입니다...</div>
        </div>

        <!-- 차트 표시 -->
        <apexchart v-show="!loading" type="bar" :height="300" :key="chartKey" :options="donutOptions.chartOptions" :series="donutOptions.series"></apexchart>
      </div>
    </div>
  </vx-card>
</template>

<script>
export default {
  name: "VBarChartCard",
  props: {
    title: String,
    series: Array,
    colors: {
      type: Array,
      default: () => ["#7E72F2", "#333333", "#A1A1A1"],
    },
    categories: Array,
    isMoveDetail: Boolean,
    isMoveWrite: Boolean,
    useSelectYear: Boolean,
    totalLabel: {
      type: String,
      default: "작업총계",
    },
    countLabel: {
      type: String,
      default: "공장별 작업총계",
    },
    totalCount: {
      type: String,
      default: "0",
    },
    categoryCounts: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    labelPosition: {
      type: String,
      default: "top",
    }
  },
  computed: {
    projectId() {
      return this.$route.params.proj_id;
    },
    /**
     * 화면 크기에 따른 모바일 여부 판단
     */
    isMobile() {
      return this.screenWidth <= 768;
    },
    /**
     * 최근 5년부터 내년까지 옵션
     */
    yearOptions() {
      const currentYear = new Date().getFullYear();
      const startYear = currentYear - 5;
      const options = [];
      for (let i = 0; i < 7; i++) {
        options.push({ label: `${startYear + i}년`, value: startYear + i });
      }

      return options.reverse();
    },
    maxY() {
      let max = 0;
      this.series.forEach((item) => {
        item.data.forEach((data) => {
          if (data > max) max = data;
        });
      });

      // 최대값을 5의 배수로 올림
      return Math.ceil(max / 5) * 5;
    },
    tickAmount() {
      const maxY = this.maxY;

      // maxY의 약수 중 5 이상인 가장 작은 값을 찾기
      for (let i = 3; i <= 8; i++) {
        if (maxY % i === 0) return i;
      }

      return 5; // 기본값은 5로 설정 (fallback)
    },
    donutOptions() {
      return {
        series: this.series,
        chartOptions: {
          chart: {
            type: "bar",
            stacked: false,
            toolbar: {
              show: false, // 툴바(다운로드 버튼) 숨기기
            },
            animations: {
              enabled: true,
              easing: "easeinout",
              speed: 800,
              animateGradually: {
                enabled: true,
                delay: 150,
              },
              dynamicAnimation: {
                enabled: true,
                speed: 350,
              },
            },
          },
          plotOptions: {
            bar: {
              horizontal: false,
              columnWidth: "70%",
              endingShape: "flat",
              dataLabels: {
                position: this.labelPosition,
                hideOverflowingLabels: false,
              },
            },
          },
          dataLabels: {
            enabled: true,
            style: {
              fontSize: "12px",
              colors: ["#333"],
            },
            offsetY: -20,
          },
          xaxis: {
            categories: this.categories,
            labels: {
              rotate: 0,
              trim: true,
              hideOverlappingLabels: false,
              showDuplicates: true,
              style: {
                fontSize: "11px",
              },
            },
          },
          yaxis: {
            min: 0,
            max: this.maxY,
            tickAmount: this.tickAmount, // 동적으로 계산된 값 사용
            labels: {
              formatter: (val) => Math.round(val), // 자연수로 표시
            },
            forceNiceScale: true, // 깔끔한 간격 강제
          },
          colors: this.colors,
          legend: { position: "bottom" },
          fill: { opacity: 1 },
        },
      };
    },
  },
  data() {
    return {
      chartKey: 0,
      screenWidth: 0,
      select: {
        year: new Date().getFullYear(),
      },
    };
  },
  mounted() {
    this.updateScreenWidth();
    window.addEventListener("resize", this.updateScreenWidth);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.updateScreenWidth);
  },
  methods: {
    updateScreenWidth() {
      this.screenWidth = window.innerWidth;
    },
    onClickDetail() {
      this.$emit("detail");
    },
    onClickWrite() {
      this.$emit("write");
    },
    onSelect(option) {
      this.$emit(`select:${option}`, this.select[option]);
    },
  },
};
</script>

<style lang="scss" scoped>
.v-bar-chart-card-header {
  display: flex;
  justify-content: space-between;
  align-items: start;
}

.circle {
  width: 12px;
  height: 12px;
  border: 2px solid;
  border-radius: 50%;

  &.purple {
    border-color: #7e72f2;
  }
  &.grey {
    border-color: #a1a1a1;
  }
}

.chart-container {
  position: relative;
  min-height: 300px;
}

.loading-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 300px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: rgba(255, 255, 255, 0.8);
  z-index: 10;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(126, 114, 242, 0.3);
  border-radius: 50%;
  border-top: 4px solid #7e72f2;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

.loading-text {
  font-size: 14px;
  color: #333;
  font-weight: 500;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

@media (max-width: 768px) {
  .v-bar-chart-card {
    margin: 0 6px;
    width: calc(100% - 12px);
  }
}

/* PC 버전 */
.flex gap-4 {
  display: flex;
  gap: 4px;
}

/* 모바일 버전 */
.mobile-layout {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 12px;
}

.mobile-layout .count-label {
  white-space: nowrap;
}

.mobile-layout .flex items-center gap-2 {
  display: flex;
  align-items: center;
  gap: 4px;
}

.mobile-layout .mobile-counts {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 8px;
  align-self: flex-start;
}

.mobile-layout .mobile-count-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  background-color: #f8f9fa;
  padding: 0;
}

.mobile-layout .mobile-count-item .count-value {
  font-weight: bold;
  color: #333;
}
</style>
